import {Injectable} from '@angular/core';
import {VexLayoutService} from '@vex/services/vex-layout.service';
import {NavigationItem} from './navigation-item.interface';
import {BehaviorSubject, Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationLoaderService {
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(private readonly layoutService: VexLayoutService) {
    this.loadNavigation();
  }

  loadNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Dashboard',
        availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT', 'AUDITOR'],
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/home',
            icon: 'mat:home',
            routerLinkActiveOptions: { exact: true },
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT', 'AUDITOR']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'General',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            type: 'dropdown',
            label: 'Admins',
            icon: 'mat:admin_panel_settings',
            availableTo: ['SUPER_ADMIN'],
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/admin/add',
                availableTo: ['SUPER_ADMIN']
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/admin/list',
                availableTo: ['SUPER_ADMIN']
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Agents',
            icon: 'mat:perm_contact_calendar',
            availableTo: ['SUPER_ADMIN', 'ADMIN'],
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/agent/add',
                availableTo: ['SUPER_ADMIN', 'ADMIN']
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/agent/list',
                availableTo: ['SUPER_ADMIN', 'ADMIN']
              }
            ]
          },
          {
            type: 'dropdown',
            label: 'Auditors',
            icon: 'mat:perm_identity',
            availableTo: ['SUPER_ADMIN', 'ADMIN'],
            children: [
              {
                type: 'link',
                label: 'Add',
                route: '/general/auditor/add',
                availableTo: ['SUPER_ADMIN', 'ADMIN']
              },
              {
                type: 'link',
                label: 'List',
                route: '/general/auditor/list',
                availableTo: ['SUPER_ADMIN', 'ADMIN']
              }
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Stages',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/stage/add',
            icon: 'mat:playlist_add',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            type: 'link',
            label: 'List',
            route: '/stage/list',
            icon: 'mat:playlist_add_check',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Reasons',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            type: 'link',
            label: 'Add',
            route: '/reason/add',
            icon: 'mat:playlist_add',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            type: 'link',
            label: 'List',
            route: '/reason/list',
            icon: 'mat:playlist_add_check',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Template',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            type: 'link',
            label: 'SMS',
            route: '/template/sms',
            icon: 'mat:sms',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            type: 'link',
            label: 'WhatsApp',
            route: '/template/whatsapp',
            icon: 'mat:chat',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Campaigns',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            icon: 'mat:campaign',
            type: 'link',
            label: 'Meta',
            route: '/campaigns/meta',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            icon: 'mat:campaign',
            type: 'link',
            label: 'Google',
            route: '/campaigns/google',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            icon: 'mat:campaign',
            type: 'link',
            label: 'LinkedIn',
            route: '/campaigns/linkedin',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Leads',
        availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT'],
        children: [
          {
            type: 'link',
            label: 'Manual',
            route: '/lead/manual',
            icon: 'mat:ballot',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          },
          {
            type: 'link',
            label: 'Facebook',
            route: '/lead/facebook',
            icon: 'mat:thumb_up',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          },
          {
            type: 'link',
            label: 'Messenger',
            route: '/lead/messenger',
            icon: 'mat:message',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          },
          {
            type: 'link',
            label: 'Instagram',
            route: '/lead/instagram',
            icon: 'mat:add_a_photo',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          },
          {
            type: 'link',
            label: 'Google',
            route: '/lead/google',
            icon: 'mat:email',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          },
          {
            type: 'link',
            label: 'LinkedIn',
            route: '/lead/linkedin',
            icon: 'mat:business',
            availableTo: ['SUPER_ADMIN', 'ADMIN', 'AGENT']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Audit',
        availableTo: ['SUPER_ADMIN', 'AUDITOR'],
        children: [
          {
            type: 'link',
            label: 'Recordings',
            route: '/audit/recordings',
            icon: 'mat:record_voice_over',
            availableTo: ['SUPER_ADMIN', 'AUDITOR', 'ADMIN']
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Support Ticket',
        availableTo: ['SUPER_ADMIN', 'AUDITOR', 'AGENT'],
        children: [
          {
            type: 'link',
            label: 'List',
            route: '/support-ticket',
            icon: 'mat:contact_support',
            availableTo: ['SUPER_ADMIN', 'AUDITOR', 'AGENT']
          }
        ]
      }
      /*{
        type: 'subheading',
        label: 'Bulk',
        availableTo: ['SUPER_ADMIN', 'ADMIN'],
        children: [
          {
            type: 'link',
            label: 'SMS',
            route: '/bulk/sms',
            icon: 'mat:sms',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          },
          {
            type: 'link',
            label: 'WhatsApp',
            route: '/bulk/whatsapp',
            icon: 'mat:chat',
            availableTo: ['SUPER_ADMIN', 'ADMIN']
          }
        ]
      },*/
    ]);
  }
}
